import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CheckCircle2, AlertCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

type Status = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const Unsubscribe = () => {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    const supabaseUrl = (import.meta as any).env.VITE_SUPABASE_URL;
    const anonKey = (import.meta as any).env.VITE_SUPABASE_PUBLISHABLE_KEY;
    fetch(`${supabaseUrl}/functions/v1/handle-email-unsubscribe?token=${token}`, {
      headers: { apikey: anonKey },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.valid === false && d.reason === "already_unsubscribed") setStatus("already");
        else if (d.valid) setStatus("valid");
        else setStatus("invalid");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleConfirm = async () => {
    setStatus("loading");
    try {
      const { data, error } = await supabase.functions.invoke("handle-email-unsubscribe", { body: { token } });
      if (error) throw error;
      if (data?.success) setStatus("success");
      else if (data?.reason === "already_unsubscribed") setStatus("already");
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        {status === "loading" && <Loader2 className="mx-auto animate-spin text-muted-foreground" size={32} />}
        {status === "valid" && (
          <>
            <AlertCircle className="mx-auto text-primary" size={40} />
            <h1 className="text-xl font-bold text-foreground font-serif">Unsubscribe</h1>
            <p className="text-sm text-muted-foreground">Are you sure you want to unsubscribe from our emails?</p>
            <Button onClick={handleConfirm} className="rounded-full px-8">Confirm Unsubscribe</Button>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-green-500" size={40} />
            <h1 className="text-xl font-bold text-foreground font-serif">Unsubscribed</h1>
            <p className="text-sm text-muted-foreground">You won't receive any more emails from us.</p>
          </>
        )}
        {status === "already" && (
          <>
            <CheckCircle2 className="mx-auto text-muted-foreground" size={40} />
            <h1 className="text-xl font-bold text-foreground font-serif">Already Unsubscribed</h1>
            <p className="text-sm text-muted-foreground">You've already unsubscribed from our emails.</p>
          </>
        )}
        {status === "invalid" && (
          <>
            <XCircle className="mx-auto text-destructive" size={40} />
            <h1 className="text-xl font-bold text-foreground font-serif">Invalid Link</h1>
            <p className="text-sm text-muted-foreground">This unsubscribe link is invalid or expired.</p>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle className="mx-auto text-destructive" size={40} />
            <h1 className="text-xl font-bold text-foreground font-serif">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">Please try again later.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
